import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { CARGO_TYPES, canShipCarryCargo } from '../../config/cargoTypes.js';
import { formatMoney, formatDate, formatDuration } from '../../utils/formatting.js';
import { showAlert }    from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';

/**
 * Contract cards shared by the Contracts tab (ContractPanel) and the port
 * panel's Contracts tab (PortPanel).
 */

/** HTML for a list of contract cards. `tab` is 'available' | 'active' | 'completed'. */
export function renderContractCards(items, tab) {
  const allShips = GameState.getAllShips();
  return items.map(c => _renderCard(c, tab, allShips)).join('');
}

/** Wire Accept buttons and port links inside `root`; `onAccepted` runs after a successful accept. */
export function bindContractCardHandlers(root, onAccepted) {
  root.querySelectorAll('[data-accept]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const contractId = btn.dataset.accept;
      const select = root.querySelector(`.contract-ship-select[data-contract="${contractId}"]`);
      if (!select) return;

      const shipId = select.value;
      if (!shipId) {
        showAlert('Select a ship first.');
        return;
      }

      btn.disabled = true;
      const result = await serverIntent('contract/accept', { contractId, shipId });
      btn.disabled = false;
      if (result.success) onAccepted?.();
      // serverIntent shows toast on failure
    });
  });

  // Port name click → zoom to port on map
  root.querySelectorAll('[data-zoom-port]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const port = GameState.getPort(el.dataset.zoomPort);
      if (port) EventBus.emit('ui:portSelected', { port });
    });
  });
}

function _renderCard(c, tab, allShips) {
  const origin = GameState.getPort(c.originPort);
  const dest   = GameState.getPort(c.destinationPort);
  const dist   = c.calculateDistance();
  const eta    = c.estimateDuration(12);
  const cargoDef = CARGO_TYPES[c.cargoType];
  const cargoName = cargoDef?.name || c.cargoType;
  const cargoCategory = cargoDef?.category || '';
  const compatShips = allShips.filter(s => canShipCarryCargo(s.type, c.cargoType));
  const eligible = tab !== 'available' || compatShips.length > 0;

  // Check which ships can carry this cargo
  let compatInfo = '';
  if (tab === 'available') {
    if (compatShips.length === 0) {
      compatInfo = '<span class="contract-compat contract-no-ship" title="No compatible ships in fleet">No compatible ships</span>';
    } else {
      const atOrigin = compatShips.filter(s => s.status === 'docked' && s.currentPortId === c.originPort && s.getAvailableCapacity() >= c.quantity);
      if (atOrigin.length > 0) {
        compatInfo = `<span class="contract-compat contract-ready" title="${atOrigin[0].name} ready at origin">${atOrigin[0].name} ready</span>`;
      } else {
        compatInfo = `<span class="contract-compat" title="${compatShips.length} ship(s) can carry this">${compatShips.length} ship${compatShips.length > 1 ? 's' : ''} compatible</span>`;
      }
    }
  }

  // Active tab: show pickup/delivery status
  let statusInfo = '';
  if (tab === 'active') {
    const assignedShip = c.acceptedShipId ? GameState.getShip(c.acceptedShipId) : null;
    const shipLabel = assignedShip ? assignedShip.name : 'Unknown ship';
    if (!c.cargoLoaded) {
      statusInfo = `<span class="contract-status contract-awaiting">Ship: ${shipLabel} — Awaiting pickup at ${origin?.name || '?'}</span>`;
    } else {
      statusInfo = `<span class="contract-status contract-enroute">Ship: ${shipLabel} — En route to ${dest?.name || '?'}</span>`;
    }
  }

  // Available tab: ship selector + accept button
  let actions = '';
  if (tab === 'available' && eligible) {
    const compatShipsList = compatShips.filter(s => s.getAvailableCapacity() >= c.quantity);
    if (compatShipsList.length > 0) {
      const options = compatShipsList.map(s => {
        const loc = s.status === 'docked'
          ? (s.currentPortId === c.originPort ? '★ at origin' : GameState.getPort(s.currentPortId)?.name || 'docked')
          : 'sailing';
        return `<option value="${s.id}">${s.name} (${loc})</option>`;
      }).join('');
      actions = `<select class="contract-ship-select" data-contract="${c.id}">${options}</select>
        <button class="btn btn-sm btn-primary" data-accept="${c.id}">Accept</button>`;
    } else {
      actions = `<button class="btn btn-sm btn-disabled" disabled>No suitable ships</button>`;
    }
  }

  const cardClass = eligible ? 'contract-card' : 'contract-card contract-card--ineligible';
  const cats = cargoDef?.requiredShipCategories;
  const shipCat = cats && cats.length > 0 ? cats[0] : 'any';

  let deadlineHtml = '';
  if (c.deadline) {
    const daysLeft = Math.ceil((new Date(c.deadline) - GameState.currentDate) / 86400000);
    const urgency = daysLeft <= 3 ? 'contract-urgent' : daysLeft <= 7 ? 'contract-soon' : '';
    deadlineHtml = `<span>Deadline: ${formatDate(c.deadline)} <small class="${urgency}">(${daysLeft}d left)</small></span>`;
  }

  return `<div class="${cardClass}">
    <div class="contract-header">
      <strong><span class="port-link" data-zoom-port="${c.originPort}">${origin?.name || '?'}</span> → <span class="port-link" data-zoom-port="${c.destinationPort}">${dest?.name || '?'}</span></strong>
      <span class="contract-pay">${formatMoney(c.payment)}</span>
    </div>
    <div class="contract-body">
      <span>${c.quantity} TEU <strong>${cargoName}</strong> <small class="contract-cat">${cargoCategory}</small> <span class="contract-ship-badge badge-${shipCat}">${shipCat}</span></span>
      <span>~${dist.toFixed(0)} nm &nbsp; ~${formatDuration(eta)}</span>
      ${deadlineHtml}
      ${compatInfo}
      ${statusInfo}
    </div>
    ${actions ? `<div class="contract-actions">${actions}</div>` : ''}
  </div>`;
}
